import React, { useMemo } from 'react';
import { Box, Grid, Typography, Paper, Stack, Avatar, Tooltip } from '@mui/material';
import {
  Description as DescriptionIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { StatusChip, formatNumber } from './CommonComponents';

/**
 * 개선된 수주품의서 개요/요약 컴포넌트 (V2)
 * 
 * 개선사항:
 * - 카드형 레이아웃
 * - 상태를 시각적 Chip으로 표현
 * - 주요 정보 강조
 * - 금액 정보 추가
 */
const OrderBookSummaryV2 = ({ orderInfo, approvalList, project }) => {
  // 최종 승인일자 계산
  const finalApproveDate = useMemo(() => {
    if (approvalList && approvalList.length > 0) {
      return approvalList[approvalList.length - 1]?.docuDt || '';
    }
    return '';
  }, [approvalList]);

  // 진행률 계산 (결재 진행 상황)
  const approvalProgress = useMemo(() => {
    if (!approvalList || approvalList.length === 0) return 0;
    const completed = approvalList.filter(a => a.docuStat === 'Y').length;
    return (completed / approvalList.length) * 100;
  }, [approvalList]);

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        mb: 3, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%)',
      }}
    >
      {/* 상단: 문서번호 & 상태 */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 3,
      }}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            문서번호
          </Typography>
          <Typography variant="h5" fontWeight={700} color="primary.main">
            {orderInfo?.roNo || '-'}
          </Typography>
        </Box>
        <StatusChip status={orderInfo?.ordYn} size="medium" />
      </Box>

      {/* 중간: 주요 정보 그리드 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* 문서명 */}
        <Grid item xs={6} sm={3}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <DescriptionIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              문서명
            </Typography>
          </Stack>
          <Typography variant="body1" fontWeight={500}>
            수주품의서
          </Typography>
        </Grid>

        {/* 작성자 */}
        <Grid item xs={6} sm={3}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              작성자
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
              {orderInfo?.crtNm?.[0] || '?'}
            </Avatar>
            <Typography variant="body1" fontWeight={500}>
              {orderInfo?.crtNm || '-'}
            </Typography>
          </Stack>
        </Grid>

        {/* 작성일자 */}
        <Grid item xs={6} sm={3}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              작성일자
            </Typography>
          </Stack>
          <Typography variant="body1" fontWeight={500}>
            {orderInfo?.crtDt || '-'}
          </Typography>
        </Grid>

        {/* 최종승인일 */}
        <Grid item xs={6} sm={3}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <CheckCircleIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              최종승인일
            </Typography>
          </Stack>
          <Typography 
            variant="body1" 
            fontWeight={500}
            color={finalApproveDate ? 'success.main' : 'text.secondary'}
          >
            {finalApproveDate || '미승인'}
          </Typography>
        </Grid>
      </Grid>

      {/* 하단: 금액 정보 (프로젝트 정보가 있을 경우) */}
      {project && (
        <Box sx={{ 
          pt: 2, 
          borderTop: '1px dashed',
          borderColor: 'divider',
        }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                총 매출액
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatNumber(project?.totalSales)}원
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                총 매입액
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatNumber(project?.totalPurchase)}원
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                매출이익
              </Typography>
              <Typography variant="h6" fontWeight={600} color="success.main">
                {formatNumber(orderInfo?.roProfitAmt || project?.totalProfit)}원
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary" display="block">
                이익률
              </Typography>
              <Typography variant="h6" fontWeight={600} color="primary.main">
                {project?.totalSales 
                  ? ((orderInfo?.roProfitAmt || project?.totalProfit) / project.totalSales * 100).toFixed(1)
                  : 0
                }%
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default OrderBookSummaryV2;
